// @ts-nocheck
import { initializeNativeHorizontalScroll } from '@theme/lib-native-horizontal-scroll';
import {
  fetchRecommendationsSection,
  getRecommendationCacheKey,
  inFlightFetches,
  parseRecommendationLimit,
  recommendationsCache,
  resolveRecommendationProductId,
  setRecommendationCacheEntry,
} from '@theme/lib-product-recommendations';

/** @type {WeakMap<HTMLElement, AbortController>} */
const activeControllers = new WeakMap();

/**
 * @param {HTMLElement} sectionEl
 * @returns {Record<string, string>}
 */
function getGridParams(sectionEl) {
  const bool = (key) => (sectionEl.dataset[key] === 'true' ? '1' : '0');
  return {
    swipe_mobile: bool('enableSwipeMobile'),
    swipe_tablet: bool('enableSwipeTablet'),
    swipe_desktop: bool('enableSwipeDesktop'),
    columns_mobile: sectionEl.dataset.columnsMobile || '1',
    columns_tablet: sectionEl.dataset.columnsTablet || '2',
    columns_desktop: sectionEl.dataset.columnsDesktop || '4',
    grid_gap_mobile:
      sectionEl.dataset.gapMobile || sectionEl.dataset.gap || '10',
    grid_gap_tablet: sectionEl.dataset.gapTablet || '10',
    grid_gap_desktop: sectionEl.dataset.gapDesktop || '10',
    visible_mobile: sectionEl.dataset.visibleItemsMobile || '',
    visible_tablet: sectionEl.dataset.visibleItemsTablet || '',
    visible_desktop: sectionEl.dataset.visibleItemsDesktop || '',
    mobile_limit: sectionEl.dataset.mobileLimit || '4',
    tablet_limit: sectionEl.dataset.tabletLimit || '4',
    desktop_limit: sectionEl.dataset.desktopLimit || '4',
    show_hint: sectionEl.dataset.showHint === '1' ? '1' : '0',
  };
}

/**
 * @param {HTMLElement} sectionEl
 */
function initHorizontalScrollInSection(sectionEl) {
  const body = sectionEl.querySelector('[data-product-recommendations-body]');
  if (!(body instanceof HTMLElement)) return;

  body.querySelectorAll('[data-horizontal-scroll]').forEach((scrollRoot) => {
    if (!(scrollRoot instanceof HTMLElement)) return;
    delete scrollRoot.dataset.hsNativeInitialized;
    initializeNativeHorizontalScroll(scrollRoot);
  });
}

/**
 * @param {HTMLElement} sectionEl
 * @param {string} cacheKey
 * @returns {boolean}
 */
function hasLoadedGrid(sectionEl, cacheKey) {
  return (
    sectionEl.dataset.loadedKey === cacheKey &&
    sectionEl.querySelector('[data-product-recommendations-rendered]') !== null
  );
}

/**
 * @param {HTMLElement} sectionEl
 * @returns {HTMLElement | null}
 */
function getFallbackGrid(sectionEl) {
  const fallbackTemplate = sectionEl.querySelector(
    '[data-product-recommendations-fallback]'
  );
  if (!(fallbackTemplate instanceof HTMLTemplateElement)) return null;

  const rendered = fallbackTemplate.content.querySelector(
    '[data-product-recommendations-rendered]'
  );
  if (!(rendered instanceof HTMLElement)) return null;

  const hasProducts = rendered.querySelector('.product-card') !== null;

  return hasProducts ? rendered : null;
}

/**
 * @param {HTMLElement} sectionEl
 * @param {string} cacheKey
 */
function injectFallbackGrid(sectionEl, cacheKey) {
  const rendered = getFallbackGrid(sectionEl);
  if (!rendered) {
    hideSection(sectionEl);
    return;
  }

  injectGrid(sectionEl, rendered, `${cacheKey}:fallback`);
}

/**
 * @param {HTMLElement} sectionEl
 */
function hideSection(sectionEl) {
  sectionEl.setAttribute('hidden', '');
  delete sectionEl.dataset.loadedKey;
}

/**
 * @param {HTMLElement} sectionEl
 * @param {HTMLElement} rendered
 * @param {string} cacheKey
 */
function injectGrid(sectionEl, rendered, cacheKey) {
  const body = sectionEl.querySelector('[data-product-recommendations-body]');
  if (!(body instanceof HTMLElement)) return;

  const gridRoot = rendered.matches('[data-product-recommendations-rendered]')
    ? rendered
    : rendered.querySelector('[data-product-recommendations-rendered]');

  if (!(gridRoot instanceof HTMLElement)) {
    if (!cacheKey.endsWith(':fallback')) {
      injectFallbackGrid(sectionEl, cacheKey);
      return;
    }

    hideSection(sectionEl);
    return;
  }

  const hasProducts = gridRoot.querySelector('.product-card') !== null;

  if (!hasProducts) {
    if (!cacheKey.endsWith(':fallback')) {
      injectFallbackGrid(sectionEl, cacheKey);
      return;
    }

    hideSection(sectionEl);
    return;
  }

  body.innerHTML = '';
  body.appendChild(gridRoot.cloneNode(true));
  body.removeAttribute('aria-busy');

  body.querySelectorAll('.product-card').forEach((card) => {
    card.classList.add('animate-in');
    card.setAttribute('data-pc-animated', 'true');
  });

  sectionEl.dataset.loadedKey = cacheKey;
  sectionEl.removeAttribute('hidden');

  initHorizontalScrollInSection(sectionEl);
}

/**
 * @param {HTMLElement} sectionEl
 * @returns {{ limit: number; intent: string; autoscroll: string }}
 */
function getSectionConfig(sectionEl) {
  return {
    limit: parseRecommendationLimit(sectionEl.dataset.limit),
    intent: sectionEl.dataset.intent || 'related',
    autoscroll: 'false',
  };
}

/**
 * @param {HTMLElement} sectionEl
 * @returns {Promise<void>}
 */
async function ensureProductRecommendations(sectionEl) {
  const productId = resolveRecommendationProductId(sectionEl);
  if (!productId) {
    injectFallbackGrid(sectionEl, 'no-product-context');
    return;
  }

  const sectionId = sectionEl.dataset.sectionId;
  if (!sectionId) {
    injectFallbackGrid(sectionEl, 'missing-section-id');
    return;
  }

  const { limit, intent, autoscroll } = getSectionConfig(sectionEl);
  const gridParams = getGridParams(sectionEl);
  const gridConfig = JSON.stringify(gridParams);
  const cacheKey = getRecommendationCacheKey({
    productId,
    limit,
    sectionId,
    intent,
    autoscroll,
    gridConfig,
  });

  if (hasLoadedGrid(sectionEl, cacheKey)) {
    sectionEl.removeAttribute('hidden');
    return;
  }

  const cached = recommendationsCache.get(cacheKey);
  if (cached) {
    injectGrid(sectionEl, cached, cacheKey);
    return;
  }

  const inFlight = inFlightFetches.get(cacheKey);
  if (inFlight) {
    await inFlight;

    if (hasLoadedGrid(sectionEl, cacheKey)) {
      sectionEl.removeAttribute('hidden');
      return;
    }

    const cachedAfterFetch = recommendationsCache.get(cacheKey);
    if (cachedAfterFetch) {
      injectGrid(sectionEl, cachedAfterFetch, cacheKey);
      return;
    }

    injectFallbackGrid(sectionEl, cacheKey);
    return;
  }

  const previous = activeControllers.get(sectionEl);
  previous?.abort();

  const controller = new AbortController();
  activeControllers.set(sectionEl, controller);

  const body = sectionEl.querySelector('[data-product-recommendations-body]');
  if (body instanceof HTMLElement) {
    body.setAttribute('aria-busy', 'true');
  }

  const fetchPromise = (async () => {
    try {
      const rendered = await fetchRecommendationsSection({
        productId,
        limit,
        sectionId,
        intent,
        autoscroll,
        gridParams,
        renderedSelector: '[data-product-recommendations-rendered]',
        signal: controller.signal,
      });

      if (!rendered) {
        injectFallbackGrid(sectionEl, cacheKey);
        return null;
      }

      setRecommendationCacheEntry(cacheKey, rendered);
      injectGrid(sectionEl, rendered, cacheKey);
      return rendered;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError')
        return null;
      injectFallbackGrid(sectionEl, cacheKey);
      return null;
    } finally {
      const active = activeControllers.get(sectionEl);
      if (active === controller) {
        activeControllers.delete(sectionEl);
      }
    }
  })();

  inFlightFetches.set(cacheKey, fetchPromise);
  await fetchPromise.finally(() => {
    inFlightFetches.delete(cacheKey);
  });
}

/**
 * @param {HTMLElement} sectionEl
 */
function initProductRecommendationsSection(sectionEl) {
  ensureProductRecommendations(sectionEl);
}

document
  .querySelectorAll('[data-product-recommendations]')
  .forEach((sectionEl) => {
    if (sectionEl instanceof HTMLElement) {
      initProductRecommendationsSection(sectionEl);
    }
  });

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    target
      .querySelectorAll('[data-product-recommendations]')
      .forEach((sectionEl) => {
        if (sectionEl instanceof HTMLElement) {
          initProductRecommendationsSection(sectionEl);
        }
      });

    if (target.matches('[data-product-recommendations]')) {
      initProductRecommendationsSection(target);
    }
  });
}
